import React, { useEffect, useState, useRef } from "react";
import "./App.css";
import "./App.scss";
import Sudowrite from "./Sudowrite.js";
import Admin from "./Admin";
import AdminSessionBanner from "./components/AdminSessionBanner";
import ReactGA from "react-ga4";
import EmailSignIn from "./EmailSignIn.js";
import EmailSignInVerify from "./components/EmailSignInVerify";
import AuthenticateMobile from "./components/AuthenticateMobile";
import Invite from "./Invite.js";
import Affiliate from "./Affiliate.js";
import CommunityManagerJobListing from "./components/CommunityManagerJobListing";
import Cookies from "universal-cookie";
import Login from "./components/Login";
import Logout from "./components/Logout";
import EventBonus from "./components/EventBonus";
import PersonalSetupRedirect from "./components/PersonalSetupRedirect";

import Auth from "./Auth";
import { CenterBox, isMobileUserAgent, makeId } from "./Utils";
import { Switch, Route, useHistory, useLocation } from "react-router-dom";

import firebase from "./Firebase.js";
import * as Sentry from "@sentry/react";
import { applyTheme } from "./lib/Theme";
import EmailVerify from "./components/EmailVerify";
import UpdatePayment from "./components/UpdatePayment";
import TaxId from "./components/TaxId";
import VatConsumerSelfCert from "./components/VatConsumerSelfCert";
import useTwitterPixelSignUpEvent from "./hooks/useTwitterPixelSignUpEvent";
import useGrowthbook from "./hooks/useGrowthbook";
import { GrowthBookAuthReadyContext } from "./lib/growthbookExperimentTracking";
import { getPlaceholderGrowthBook } from "./lib/growthbookClient";
import { IMPORT_SPEED_PERCEPTION_EXPERIMENT_KEY } from "../../src/shared/constants";
import queryString from "query-string";
import useCookieRunOnce from "./hooks/useCookieRunOnce";
import AdsLandingPage from "./components/AdsLandingPage";
import { OnlineStatusProvider } from "./hooks/useOnlineStatus";
import useCaptureURLParams from "./hooks/useCaptureURLParams";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import useHelpkit from "./hooks/useHelpkit.js";
import { useAppStore } from "./stores/appStore";
import { useAdminAccessStore } from "./stores/adminAccessStore";
import { useAdminAccess } from "./hooks/useAdminAccess";
import { getAuthUser, resolveEffectiveAuthUser, resolveAdminSessionOrSignOut } from "./lib/effectiveAuthUser";
import { isAdminSessionUid } from "../../src/shared/adminSessionIdentity";
import { useSudowriteStore } from "./stores/sudowriteStore";

import PublicPluginHost from "./components/PublicPluginHost/index.js";
import PublicPluginDetail from "./components/PublicPluginDetail/index.js";
import PluginCreator from "./components/PluginCreator/index.js";
import { ShareLinkPage } from "./components/ShareLinkPage/ShareLinkPage.js";

import { APP_DOMAIN, MULTIPLE_EMAILS_ASSOCIATED_WITH_SUBSCRIPTION_ERROR } from "../../src/shared/constants.js";
import SVG from "react-inlinesvg";
import useGoogleSignIn from "./hooks/useGoogleSignIn.js";
import Pricing from "./components/Pricing/index.js";
import SubscriptionSuccess from "./components/SubscriptionSuccess/index.js";
import useScrollTracker from "./hooks/useScrollTracker";
import useActiveScrollbarVisibility from "./hooks/useActiveScrollbarVisibility";
import DevelopmentLogin from "./components/DevelopmentLogin";
import getCastle, { getCastleRequestToken } from "./CastleConfiguration";
import mixpanel from "./MixpanelConfiguration";
import ToastWrapper from "./components/ToastWrapper/index.js";
import AppPublic from "./AppPublic.js";
import usePublicMode from "./hooks/usePublicMode.js";
import WebSocketProvider from "./components/SocketHost/WebSocketProvider";
import { ReactSelectFocusManager } from "./components/StyledSelect/ReactSelectFocusManager";
import AIFunctions from "./components/AIFunctions";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/query-client";
import { SudoLayoutEventProvider } from "./lib/layout-events";
import { WithPartnerCodeStore } from "./stores/partnerCodeStore";
import { AppEventPublisher } from "./AppEventPublisher";
import MobileRedirect from "./components/MobileRedirect";
import { filterFrontendSentryEvent } from "./sentryFilters";
import AppLoadingLogoTransition, { useAppLoadingLogoTransition } from "./components/AppLoadingLogoTransition";

Sentry.init({
  dsn: "https://ca49587fe19a490ba97434413ba38f9f@o432981.ingest.sentry.io/5453557",
  debug: process.env.REACT_APP_DEBUG_SENTRY === "true",
  release: process.env.REACT_APP_SENTRY_RELEASE,
  integrations: [
    Sentry.breadcrumbsIntegration({
      console: false,
    }),
    // Do not install httpClientIntegration here. Its default behavior is to
    // create synthetic frontend issues for every failed 5xx fetch/XHR response,
    // and those server-side failures are already tracked in backend Sentry.
  ],

  tracesSampleRate: 0,

  beforeSend: filterFrontendSentryEvent,

  ignoreErrors: [
    "TypeError: Failed to fetch",

    // This comes from Sleek changelog widget.  It can't be caught because it's
    // sent from an iframe.
    "ReferenceError: final_height is not defined",
  ],
  environment: process.env.NODE_ENV === "production" ? "production" : "development",
});

window.cacheBustingFixLoaded = true;

function Main({
  openLogin, //
  user,
  authRedirectResult,
  authRedirectResultError,
  rewardfulId,
  showFront,
  hideFront,
}) {
  const [loading, setLoading] = useState(false);
  const [loadingLong, setLoadingLong] = useState(false);
  const [isSudowriteReady, setIsSudowriteReady] = useState(false);
  const [isSudowriteDarkTheme, setIsSudowriteDarkTheme] = useState(false);
  const {
    shouldShowTransition: shouldShowAppLoadingTransition,
    isAppContentWaitingForTransition,
    handleTransitionActiveChange,
    handleTransitionComplete,
  } = useAppLoadingLogoTransition({
    userId: user?.uid,
    isAppReady: !loading && isSudowriteReady,
    isAppMountBlocked: loading,
  });
  // const [version, setVersion] = useState(null);
  const version = useRef(null);
  const [newVersion, setNewVersion] = useState(null);
  const [loginError, setLoginError] = useState(null);

  useScrollTracker();

  useEffect(() => {
    if (loading || !user) {
      setIsSudowriteReady(false);
    }
  }, [loading, user]);

  // Check version.json for new versions and display a message to refresh if so
  const checkVersion = () => {
    fetch("/version.json")
      .then((response) => response.json())
      .then((data) => {
        if (data.version) {
          if (!version.current) {
            version.current = data.version;
            window.sudowriteClientVersion = data.version;
          } else if (data.version !== version.current) {
            setNewVersion(data);

            // if version is forced on, then we force reload
            if (data.force) {
              const minDelay = 60 * 1000; // X seconds
              const maxDelay = 6 * 60 * 1000; // X minutes
              const delay = Math.random() * (maxDelay - minDelay) + minDelay;

              setTimeout(() => {
                // redirect to new domain if necessary
                if (data.redirect && window.location.hostname !== APP_DOMAIN) {
                  window.location.href = `https://${APP_DOMAIN}${window.location.pathname}`;
                } else {
                  window.location.reload();
                }
              }, delay);
            }
          }
        }
      });
  };

  const subscriptionSource = window.localStorage.getItem("subscription-source");

  const runOnce = useCookieRunOnce();

  // Checks log in state
  useEffect(() => {
    const cookies = new Cookies();

    const sentryTimeout = setTimeout(() => {
      setLoadingLong(true);
      Sentry.captureMessage("Showing loading message on login because taking too long (10 seconds)", {
        tags: {
          user: user?.uid,
          userEmail: user?.email,
          authRedirectResultError: authRedirectResultError,
          authRedirectResult: authRedirectResult,
        },
      });
    }, 10000);

    const clearTimeouts = () => {
      // console.log("Calling clearTimeouts");
      // clearTimeout(loadingTimeout);
      clearTimeout(sentryTimeout);
      setLoading(false);
      setLoadingLong(false);
    };

    if (user) {
      mixpanel.identify(user.uid);
      // console.log("Log in State useEffect called with user: " + user?.uid);
      // console.log("state: loading", loading);

      if (authRedirectResultError) {
        // console.log("authRedirectResultError", authRedirectResultError);
        clearTimeouts();
        setLoginError(authRedirectResultError);
      } else {
        // console.log("userAuth", user.uid);
        // console.log("authRedirectResult", authRedirectResult);
        // console.log("operationType", authRedirectResult?.operationType);

        // console.log("calling /api/access");

        if (window.edgetag) {
          window.edgetag("user", "email", user.email);
        }
        getCastleRequestToken().then((token) => {
          // Compare the resolved identity: an admin session's credential UID
          // deliberately differs from the customer UID used by the app.
          if (getAuthUser() !== user) return;
          const partnerCode = cookies.get("partner_code") || cookies.get("affiliate") || localStorage.getItem("partner_code");
          const partnerName = localStorage.getItem("partner_name");

          Auth.fetchWithIdToken("/api/access", {
            user: user,
            affiliateCode: cookies.get("affiliate"),
            rewardfulId: rewardfulId,
            source: subscriptionSource,
            castleToken: token,
            prefilledProjectCode: cookies.get("prefilled_project") || null,
            partnerCode: partnerCode,
            partnerName: partnerName,
            browserLocale: navigator.language || null,
          })
            .then((result) => {
              // console.log("result", result);

              if (!result.success) {
                Sentry.captureMessage("/api/access returned success false!", {
                  tags: {
                    user: user?.uid,
                    userEmail: user?.email,
                    authRedirectResultError: authRedirectResultError,
                    authRedirectResult: authRedirectResult,
                  },
                });
              }

              window.ca?.identify?.(user.uid, {
                email: user?.email,
                name: user?.displayName,
              });

              if (result.new_user) {
                window.ca?.track?.("signup");

                runOnce(() => {
                  if (window.dataLayer) {
                    window.dataLayer.push({
                      event: "registration",
                      eventCategory: "registration",
                      eventAction: "registration",
                      eventLabel: "new user",
                    });
                  }
                  if (window.edgetag) {
                    // console.log("[EDGETAG REGISTRATION]");
                    window.edgetag("tag", "CompleteRegistration", {
                      name: "Sudowrite",
                    });
                  }
                }, `new-user-signup-event-${user.uid}`);
              } else {
                window.ca?.track?.("signin");
              }

              clearTimeouts();
              setLoading(false);
              setLoadingLong(false);

              if (result.has_prefilled_project && result.prefilled_project_code) {
                window.sessionStorage.setItem("prefilled_project_code", result.prefilled_project_code);

                cookies.remove("prefilled_project", {
                  domain: process.env.NODE_ENV === "production" ? ".sudowrite.com" : "localhost",
                  path: "/",
                });
              }

              // Start versioning check every minute
              checkVersion();
              setInterval(checkVersion, 1000 * 60 * 1);

              document.body.classList.add("app");

              // console.log("User logged in / created and app should be showing now");
            })
            .catch(async (error) => {
              console.log("Call to /api/access failed");
              console.log("Error", error);

              let responseJson = null;
              if (error.response) {
                try {
                  responseJson = await error.response.clone().json();
                } catch (_jsonError) {
                  // ignore JSON parsing issues; fall back to default error message
                }
              }

              if (responseJson?.error === MULTIPLE_EMAILS_ASSOCIATED_WITH_SUBSCRIPTION_ERROR) {
                setLoginError(
                  "We couldn't finish signing you in because your subscription is already linked to another email. Please reach out to hi@sudowrite.com so we can help."
                );
              } else {
                setLoginError(true);
              }
            });
        });
      }
    } else {
      // check for a redirectlink from storage -- used when logged out from share link page
      const redirectLink = window.localStorage.getItem("redirectLink");
      if (redirectLink) {
        clearTimeouts();
        window.localStorage.removeItem("redirectLink");
        window.location.href = redirectLink;
      } else {
        clearTimeouts();
        openLogin({ silent: true });
      }
    }

    return () => {
      // console.log("Main Unmount");
      // on unmount
      if (document?.body) {
        document.body.classList.remove("app");
      }

      clearTimeouts();
    };
  }, [user, authRedirectResultError]);

  if (loadingLong) {
    return (
      <CenterBox>
        Looks like loading is taking a long time... if nothing happens try reloading the page. We recommend disabling browser extensions, VPN, and any antivirus software running your computer.
      </CenterBox>
    );
  }

  if (loginError) {
    const defaultMessage =
      "Oops! There was an error signing in. Please ensure your device is connected to the internet, disable browser extensions, VPN, and antivirus software, then refresh the page to try again.";
    const message = typeof loginError === "string" ? loginError : defaultMessage;
    return (
      <CenterBox>
        <div>{message}</div>
      </CenterBox>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <>
      {shouldShowAppLoadingTransition && (
        <AppLoadingLogoTransition
          key={`app-loading-logo-${user.uid}`}
          isDarkTheme={isSudowriteDarkTheme}
          isAppReady={!loading && isSudowriteReady}
          onActiveChange={handleTransitionActiveChange}
          onComplete={handleTransitionComplete}
        />
      )}

      {!loading && (
        <QueryClientProvider client={queryClient}>
          {newVersion && (
            <div id="new-version">
              <div className="header">
                <SVG src="/assets/sudowrite_update.svg" />
                <div className="title">{newVersion.title || "Sudowrite Update"}</div>
              </div>
              <hr />
              <div className="body">
                <p>{newVersion.message || "A new version of Sudowrite is available."}</p>

                {newVersion.force && !newVersion.redirect && (
                  <p>
                    In a moment, this page will refresh. Or you can <a href="/app">click here to refresh now</a>.
                  </p>
                )}

                {newVersion.force && newVersion.redirect && (
                  <p>
                    In a moment, you will be redirected to <a href={`https://${APP_DOMAIN}/`}>{APP_DOMAIN}</a>, and you will have to log in again. (Don't worry, all your work is safe.) Or you can{" "}
                    <a href={`https://${APP_DOMAIN}/`}>click here to go now</a>.
                  </p>
                )}

                {!newVersion.force && (
                  <>
                    Please <a href="/app">refresh</a> to get the latest update.
                  </>
                )}

                <p className="footer">Contact hi@sudowrite.com if you have any questions.</p>
              </div>
            </div>
          )}

          <Sudowrite
            showFront={showFront}
            hideFront={hideFront}
            user={user}
            setNeedsUserCreation={setLoading}
            isAppLoadingTransitionActive={isAppContentWaitingForTransition}
            onAppReadyChange={setIsSudowriteReady}
            onAppDarkThemeChange={setIsSudowriteDarkTheme}
          />
        </QueryClientProvider>
      )}
    </>
  );
}

function MaybeGrowthBookProvider({ growthbook, authReady, children }) {
  return (
    <GrowthBookAuthReadyContext.Provider value={authReady}>
      <GrowthBookProvider growthbook={growthbook || getPlaceholderGrowthBook()}>{children}</GrowthBookProvider>
    </GrowthBookAuthReadyContext.Provider>
  );
}

function AppFull() {
  const { can: canAdmin, loading: adminAccessLoading } = useAdminAccess();
  const user = useAppStore((state) => state.user);
  const setUser = useAppStore((state) => state.setUser);
  const setAnonUser = useAppStore((state) => state.setAnonUser);
  const loadedUser = useAppStore((state) => state.loadedUser);
  const setLoadedUser = useAppStore((state) => state.setLoadedUser);
  const reset = useAppStore((state) => state.reset);

  // Local state variables
  const [loginOpen, setLoginOpen] = useState(window.location.hash === "#login");
  const [loginClosable, setLoginClosable] = useState(true);
  const [loggedInPath, setLoggedInPath] = useState(false);

  const [authRedirectResult, setAuthRedirectResult] = useState(null);
  const [authRedirectResultError, setAuthRedirectResultError] = useState(null);
  const [adminSessionError, setAdminSessionError] = useState(null);
  const [loginTitle, setLoginTitle] = useState(null);

  const history = useHistory();
  const location = useLocation();
  const rewardfulId = useRef(null);

  // Uses localstorage to avoid a redirct race condition that sometimes
  // incorrectly registers subscription as source:web-app first when just using window.location
  const subscriptionSource = window.localStorage.getItem("subscription-source");
  const isAppScreen = window.location.pathname == "/app";
  if (!subscriptionSource && isAppScreen) {
    const source = isMobileUserAgent ? "web-app-mobile" : "web-app";
    window.localStorage.setItem("subscription-source", source);
  }

  const clientId = useRef(null);

  useCaptureURLParams();

  const [showFront, hideFront] = useHelpkit();

  useEffect(() => {
    return () => reset();
  }, []);

  useEffect(() => {
    const cookies = new Cookies();

    // Set a unique client id
    if (cookies.get("client_id")) {
      clientId.current = cookies.get("client_id");
    } else {
      clientId.current = makeId(25);

      cookies.set("client_id", clientId.current, {
        domain: process.env.NODE_ENV === "production" ? ".sudowrite.com" : "localhost",
        maxAge: 60 * 60 * 24 * 365 * 4,
        path: "/",
      });
    }

    // Apply theme
    applyTheme("sorbet"); // resets theme to sorbet

    ReactGA.initialize("G-ZXDVK9PG5W");

    window.edgetag("tag", "PageView");

    getCastle();
  }, []);

  useEffect(() => {
    if (window.rewardful) {
      window.rewardful("ready", function () {
        if (window.Rewardful.referral) {
          rewardfulId.current = window.Rewardful.referral;
        }
      });
    }
  }, []);

  // If user is authed and not at an authed resource, automatically redirect to /app
  useEffect(() => {
    // these pages do their own redirects, so we don't redirect for them
    if (
      location.pathname.startsWith("/email-") || //
      location.pathname.startsWith("/a/") ||
      location.pathname.startsWith("/invite/") ||
      location.pathname.startsWith("/logout") ||
      location.pathname.startsWith("/event-bonus") ||
      location.pathname.startsWith("/personal-setup-session") ||
      location.pathname.startsWith("/pages")
    ) {
      return;
    }

    // paths which we don't need to redirect
    const loggedInPaths = [
      { path: "/" }, // "Try sudowrite for free" redirects here, so we don't provide a title
      { path: "/app", title: "Log in to Sudowrite" }, // "Log in to Sudowrite" redirects here, so we do provide a title
      { path: "/event-bonus" },
      { path: "/admin" },
      { path: "/app/subscription", title: "Log in to Update Your Payment Info" },
      { path: "/development-login" },
      { path: "/pages" }, // /pages/* are authed pages — keep loginOpen true after a logout (e.g. switch-account)
    ];

    let isAtLoggedInPath = false;

    for (const path of loggedInPaths) {
      if (location.pathname.startsWith(path.path)) {
        const isExactPluginsLocation = location.pathname === "/app/plugins" || location.pathname === "/app/plugins/";
        const isPluginsSearchLocation = location.pathname.startsWith("/app/plugins/search");
        const isPluginCategoryLocation = location.pathname.startsWith("/app/plugins/category");
        const isPluginDetailLocation = location.pathname.startsWith("/app/plugin/");
        const isPluginCreatorLocation = location.pathname.startsWith("/app/plugins/creator");
        const isSharedDocumentLocation = location.pathname.startsWith("/app/documents/");
        const isEventLocation = location.pathname.startsWith("/event/get-in/");
        const isMobileRedirectLocation = location.pathname.startsWith("/mobile-irl");
        const isDevelopmentLoginLocation = location.pathname.startsWith("/devlogin");
        const isPricingLocation = location.pathname.startsWith("/pricing");
        if (
          isEventLocation ||
          isPricingLocation ||
          isExactPluginsLocation ||
          isPluginsSearchLocation ||
          isPluginCategoryLocation ||
          isPluginDetailLocation ||
          isPluginCreatorLocation ||
          isSharedDocumentLocation ||
          isDevelopmentLoginLocation ||
          isMobileRedirectLocation
        ) {
          // an exception.
          continue;
        }
        setLoginTitle(path.title);
        isAtLoggedInPath = true;
      }
    }

    setLoggedInPath(isAtLoggedInPath);

    if (isAtLoggedInPath) {
      setLoginClosable(false);
    }

    const partnerRef = queryString.parse(location.search).partner;
    const partnerName = queryString.parse(location.search).partner_name;

    if (partnerRef) {
      window.localStorage.setItem("partner_code", partnerRef);
    }

    if (partnerName) {
      window.localStorage.setItem("partner_name", partnerName);
    }

  }, [location]);

  // Main hook to firebase that changes when user is logged in
  useEffect(() => {
    let active = true;
    let authChange = 0;
    const unsubscribe = firebase.auth().onAuthStateChanged(async (rawUser) => {
      const currentChange = ++authChange;
      setAdminSessionError(null);
      resolveEffectiveAuthUser(null);
      // Clear capabilities synchronously before rendering a different actor.
      const accessRequest = useAdminAccessStore.getState().load(rawUser?.isAnonymous ? null : rawUser?.uid);
      let userAuth = rawUser;
      if (isAdminSessionUid(rawUser?.uid)) {
        setLoadedUser(false);
        setUser(null);
        const access = await accessRequest;
        if (!active || currentChange !== authChange) return;
        // A synthetic session must be validated before it can open a customer's
        // workspace. Valid sessions retain their Firebase token identity;
        // rejected sessions must also clear the persisted Firebase login.
        try {
          userAuth = await resolveAdminSessionOrSignOut(rawUser, access);
        } catch (error) {
          userAuth = null;
          if (active && currentChange === authChange && firebase.auth().currentUser === rawUser) {
            Sentry.captureException(error);
            setAdminSessionError(error);
          }
        }
      } else {
        resolveEffectiveAuthUser(rawUser, null);
      }
      if (!active || currentChange !== authChange) return;

      if (userAuth?.isAnonymous) {
        setAnonUser(userAuth);
        setUser(null);
        setLoadedUser(true);
        return;
      }

      setUser(userAuth);
      setAnonUser(null);
      setLoadedUser(true);

      const domain = process.env.NODE_ENV === "production" ? ".sudowrite.com" : "localhost";

      if (userAuth) {
        // User is logged in, set cookie to detect on marketing site for redirect
        const cookies = new Cookies();

        cookies.set("sudowrite_logged_in", "true", { path: "/", domain });

        Sentry.setUser({ email: userAuth.email, id: userAuth.uid });

        if (window.ttq) {
          window.ttq.identify({
            email: userAuth.email,
          });
        }

        if (window.cioanalytics) {
          // identify user to customer.io for in app messaging.
          window.cioanalytics.identify(userAuth.uid, {
            email: userAuth.email,
          });
        }
      } else {
        // set up pricing v4 test

        // user is logged out, remove cookie
        const cookies = new Cookies();

        cookies.remove("sudowrite_logged_in", { path: "/", domain });
      }
    });
    return () => { active = false; unsubscribe(); };
  }, [setUser, setAnonUser, setLoadedUser]);

  // the main call to check for firebase auth redirect
  useEffect(() => {
    firebase
      .auth()
      .getRedirectResult()
      .then(async (result) => {
        setAuthRedirectResult(result);
      })
      .catch((error) => {
        setAuthRedirectResultError(error);
      });
  }, []);

  const googleSignIn = useGoogleSignIn({ history });

  useEffect(() => {
    if (!user) {
      // certain query params should automatically log in with Google without showing login
      const queries = queryString.parse(location.search);
      if (queries.GoogleLogin === "true") {
        googleSignIn();
      }

      if (queries.prefilled) {
        const cookies = new Cookies();
        cookies.set("prefilled_project", queries.prefilled, {
          domain: process.env.NODE_ENV === "production" ? ".sudowrite.com" : "localhost",
          maxAge: 60 * 60 * 24, // 24 hours
          path: "/",
        });
        window.sessionStorage.setItem("prefilled_project_code", queries.prefilled);
      }
      // show login when no user but we are at a logged in path
      setLoginOpen(loggedInPath);
    } else if (user) {
      if (!loggedInPath) {
        // for pricing, we redirect to /app/plans
        if (location.pathname.indexOf("/pricing") !== -1) {
          history.push("/app/plans");
        } else if (location.pathname.indexOf("/event-bonus") !== -1 || location.pathname.startsWith("/logout")) {
          // These routes own their destination; don't race account switching.
        } else {
          if (location.pathname.startsWith("/app/plugin") || location.pathname.startsWith("/app/documents") || location.pathname.startsWith("/pages")) {
            // do nothing
          } else {
            // else go to /app
            history.push("/app");
          }
        }
      }
    }
    if (user && location.pathname === "/faq") {
      history.push("/faq");
    }
    if (user && location.pathname === "/jobs-community") {
      history.push("/jobs-community");
    }
  }, [user, loggedInPath]);

  const { growthbook, authReady: growthbookAuthReady } = useGrowthbook({
    clientId: clientId.current,
    history,
    user,
    loadedUser,
  });
  const subscription = useSudowriteStore((state) => state.subscription);

  useEffect(() => {
    if (!growthbook) return;
    growthbook.isOn("testing-feature-experiment"); // Just to include the user in the a-a experiment
    if (!subscription?.fetched) return;
    if (subscription.activeTrial()) {
      growthbook.isOn(IMPORT_SPEED_PERCEPTION_EXPERIMENT_KEY);
    }
  }, [growthbook, subscription]);

  useTwitterPixelSignUpEvent({ user });

  function openLogin({ silent } = {}) {
    if (!silent) {
      AppEventPublisher.meta.loginModalViewed();
    }

    setLoginOpen(true);
  }

  return (
    <QueryClientProvider client={queryClient}>
      <WithPartnerCodeStore>
        <SudoLayoutEventProvider>
          <ReactSelectFocusManager>
            <MaybeGrowthBookProvider growthbook={growthbook} authReady={growthbookAuthReady}>
              <ToastWrapper>
                <AdminSessionBanner />
                <OnlineStatusProvider>
                  <WebSocketProvider user={user}>
                    {loginOpen && (
                      <Login
                        close={() => setLoginOpen(false)}
                        history={history}
                        closeable={loginClosable}
                        user={user}
                        loadedUser={loadedUser}
                        authRedirectResult={authRedirectResult}
                        authRedirectResultError={adminSessionError || authRedirectResultError}
                        title={loginTitle}
                      />
                    )}

                    <Switch>
                      {process.env.NODE_ENV !== "production" && (
                        <Route
                          exact
                          path="/devlogin"
                        >
                          <DevelopmentLogin />
                        </Route>
                      )}

                      <Route
                        exact
                        path="/learn-more"
                      >
                        <AdsLandingPage
                          clientId={clientId.current}
                          openLogin={openLogin}
                          showFront={showFront}
                          hideFront={hideFront}
                        />
                      </Route>

                      <Route
                        exact
                        path="/email-sign-in"
                      >
                        <EmailSignIn />
                      </Route>
                      <Route
                        exact
                        path="/email-sign-in-verify"
                      >
                        <EmailSignInVerify loadedUser={loadedUser} />
                      </Route>
                      <Route path="/authenticate_mobile">
                        <AuthenticateMobile />
                      </Route>
                      <Route
                        exact
                        path="/email-verify"
                      >
                        <EmailVerify />
                      </Route>
                      <Route
                        exact
                        path="/app/subscription/portal"
                      >
                        <UpdatePayment user={user} />
                      </Route>

                      <Route exact path="/pages/tax-id">
                        <TaxId user={user} openLogin={openLogin} />
                      </Route>

                      <Route exact path="/pages/vat-consumer-self-cert">
                        <VatConsumerSelfCert user={user} openLogin={openLogin} />
                      </Route>

                      <Route
                        exact
                        path={["/app/plugins", "/app/plugins/search/:query", "/app/plugins/search/", "/app/plugins/category/:categorySlug"]}
                      >
                        <PublicPluginHost openLogin={openLogin} />
                      </Route>

                      <Route
                        exact
                        path={["/app/plugin/:id/:slug", "/app/plugin/:id"]}
                      >
                        <PublicPluginDetail
                          user={user}
                          openLogin={openLogin}
                        />
                      </Route>

                      <Route
                        exact
                        path={["/app/plugins/creator/:slug"]}
                      >
                        <PluginCreator
                          user={user}
                          openLogin={openLogin}
                        />
                      </Route>

                      <Route
                        exact
                        path="/event/get-in/"
                      >
                        <Pricing
                          openLogin={() => {}}
                          showFront={() => {}}
                          hideFront={() => {}}
                        />
                      </Route>

                      <Route
                        exact
                        path="/event/get-in/success"
                      >
                        <SubscriptionSuccess />
                      </Route>

                      <Route
                        exact
                        path="/mobile-irl"
                      >
                        <MobileRedirect />
                      </Route>

                      <Route
                        exact
                        path="/logout"
                      >
                        <Logout />
                      </Route>
                      <Route
                        exact
                        path="/pricing"
                      >
                        <Pricing
                          openLogin={openLogin}
                          showFront={showFront}
                          hideFront={hideFront}
                        />
                      </Route>
                      {/* GET RID OF THE FOLLOWING ROUTES WHICH ARE REPLACED WITH FRAMER MARKETING SITE */}
                      {/* <Route
              exact
              path="/faq"
            >
              <MainFAQ
                showFront={showFront}
                hideFront={hideFront}
              />
            </Route> */}
                      <Route
                        exact
                        path="/jobs-community"
                      >
                        <CommunityManagerJobListing />
                      </Route>

                      <Route
                        exact
                        path="/invite/:code"
                      >
                        <Invite />
                      </Route>
                      <Route
                        exact
                        path="/a/:code"
                      >
                        <Affiliate />
                      </Route>
                      <Route path="/app/documents/:documentId/feedback">
                        <ShareLinkPage
                          user={user}
                          loadedUser={loadedUser}
                          openLogin={openLogin}
                          authRedirectResult={authRedirectResult}
                          authRedirectResultError={adminSessionError || authRedirectResultError}
                        />
                      </Route>
                      <Route
                        exact
                        path={["/", "/app", "/app/:path*"]}
                      >
                        <Main
                          user={user}
                          rewardfulId={rewardfulId.current}
                          openLogin={openLogin}
                          authRedirectResult={authRedirectResult}
                          showFront={showFront}
                          hideFront={hideFront}
                        />
                      </Route>
                      <Route path="/ai-functions">
                        {!loadedUser || adminAccessLoading ? <CenterBox>Loading...</CenterBox>
                          : canAdmin("admin.tools") ? <AIFunctions user={user} /> : <CenterBox>Admin access required.</CenterBox>}
                      </Route>
                      <Route path="/admin">
                        <Admin user={user} />
                      </Route>
                      <Route
                        exact
                        path="/event-bonus"
                      >
                        <EventBonus
                          user={user}
                          openLogin={openLogin}
                        />
                      </Route>
                      <Route
                        exact
                        path="/personal-setup-session"
                      >
                        <PersonalSetupRedirect />
                      </Route>
                    </Switch>
                  </WebSocketProvider>
                </OnlineStatusProvider>
              </ToastWrapper>
            </MaybeGrowthBookProvider>
          </ReactSelectFocusManager>
        </SudoLayoutEventProvider>
      </WithPartnerCodeStore>
    </QueryClientProvider>
  );
}

function App() {
  useActiveScrollbarVisibility();

  const { isPublicPluginsMode } = usePublicMode();

  if (isPublicPluginsMode) {
    return <AppPublic />;
  } else {
    return <AppFull />;
  }
}

export default App;
